import { createPortal } from 'react-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserCircle } from 'react-icons/fa';
import { Icons } from '@/utils/Icons';
import { profileDrawerItems } from '@/utils/constants';
import { toast } from 'react-toastify';
import { NavLink, useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '@/store/api/authApi';
import { unregisterPushDevice } from '@/lib/pushNotifications';
import { useAppDispatch, useLockBodyScroll } from '@/hooks';
import { logout } from '@/store';
import { apiSlice } from '@/store/api/apiSlice';
import UnsettledBetModal from '@/components/common/UnsettledBetModal';
import { useIsDemoUser, useIsMasterOwnerUser, useAppSelector } from '@/hooks/redux';
import { useGetSystemSettingsQuery } from '@/store/api/systemApi';
import { useGetUserReferenceQuery } from '@/store/api/userApi';

// profileDrawerItems ids -> translation key under profileDrawer.items
const profileDrawerItemI18nKey: Record<number, string> = {
  14: 'coinReward',
  1: 'accountStatement',
  2: 'profitLossReport',
  3: 'betHistory',
  4: 'unsettledBet',
  5: 'casinoBetHistory',
  6: 'setStake',
  7: 'rules',
  8: 'changePassword',
  9: 'results',
  10: 'exportReportHistory',
  11: 'bonusHistory',
  12: 'commission',
  13: 'referral',
};

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ProfileDrawer = ({ open, onClose }: ProfileDrawerProps) => {
  const { t } = useTranslation();
  useLockBodyScroll(open);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logoutApi] = useLogoutMutation();
  const isDemoUser = useIsDemoUser();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const username = useAppSelector((state) => state.user.profile?.username ?? '');
  const isMasterOwnerUser = useIsMasterOwnerUser();

  const balance = useAppSelector((state) => state.wallet.mainBalance);
  const exposure = useAppSelector((state) => state.wallet.exposure);
  const lockedAmount = useAppSelector((state) => state.wallet.lockedAmount);
  const bonusBalance = useAppSelector((state) => state.wallet.bonusBalance);
  const coinAmount = useAppSelector((state) => state.wallet.coinAmount);
  const netBalance = balance - Math.abs(exposure);
  const { data: systemSettings } = useGetSystemSettingsQuery(undefined, {
    pollingInterval: 30000,
    refetchOnMountOrArgChange: true,
  });
  const { data: referenceData } = useGetUserReferenceQuery(undefined, {
    skip: !open || !isAuthenticated || isDemoUser,
  });
  const totalPL = Number(referenceData?.reference ?? 0);

  const [exposureModalOpen, setExposureModalOpen] = useState(false);

  const handleClick = async () => {
    // Unregister this device's push notifications while the auth token is still
    // valid. Skipped if notifications were never enabled; failures (or a slow
    // response) must never block logout.
    await unregisterPushDevice();

    dispatch(logout());
    dispatch(apiSlice.util.resetApiState());
    logoutApi().catch(() => {});
    // Replace (not push) so the authenticated page can't be reached again
    // via the browser back button and restored from bfcache.
    window.location.replace('/');
  };
  return createPortal(
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[9998] bg-black/50 transition-opacity duration-300 ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 z-[9999] h-[100dvh] w-[320px] bg-white dark:bg-darkPanelBg shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Close Button */}
        <button onClick={onClose} className="absolute right-3 top-3 text-black dark:text-white">
          <Icons.Cross2 size={12} />
        </button>

        <div className="h-full overflow-y-auto pt-12 pb-4">
          {/* User Info */}
          <div className="flex items-center gap-2 bg-popupProfileBox text-popupProfileText h-10 mx-4 px-[10px] py-[5px] rounded-sm">
            <FaUserCircle size={20} className="text-[#1f2c38] dark:text-white" />

            <h2 className="text-[18px] font-bold text-[#1f2c38] dark:text-white">
              {username} : {netBalance.toFixed(2)}
            </h2>
          </div>
          {/* Exposure */}
          <div
            onClick={isDemoUser ? undefined : () => setExposureModalOpen(true)}
            className={`flex items-center justify-between rounded bg-exposure-gradient mx-4 my-[5px] px-[10px] py-[5px] text-white gap-[10px] ${isDemoUser ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <span className="text-lg">{t('profileDrawer.exposure')}</span>
            <span className={`text-lg ${exposure < 0 ? 'text-red-500' : 'text-[#32d338]'}`}>
              {exposure < 0 ? `-${Math.abs(exposure).toFixed(2)}` : exposure.toFixed(2)}
            </span>
          </div>
          {/* P&L */}
          <div
            onClick={
              isDemoUser
                ? undefined
                : () => {
                    onClose();
                    navigate('/profit-loss');
                  }
            }
            className={`flex items-center justify-between rounded bg-[#1f2c38] mx-4 my-[5px] px-[10px] py-[5px] text-white gap-[10px] ${isDemoUser ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <span className="text-lg">{t('profileDrawer.profitLoss')}</span>
            <span className={`text-lg ${totalPL < 0 ? 'text-red-500' : 'text-[#32d338]'}`}>
              {totalPL < 0 ? `-${Math.abs(totalPL).toFixed(2)}` : totalPL.toFixed(2)}
            </span>
          </div>
          {/* Locked Balance */}
          <div className="flex items-center justify-between rounded bg-[#1f2c38] mx-4 my-[5px] px-[10px] py-[5px] text-white gap-[10px]">
            <span className="text-lg">{t('profileDrawer.lockedAmount')}</span>
            <span className="text-lg text-[#32d338]">{lockedAmount.toFixed(2)}</span>
          </div>
          {/* Bonus Balance */}
          <div className="flex items-center justify-between rounded bg-[#1f2c38] mx-4 my-[5px] px-[10px] py-[5px] text-white gap-[10px]">
            <span className="text-lg">{t('profileDrawer.bonusBalance')}</span>
            <span className="text-lg text-[#32d338]">{bonusBalance.toFixed(2)}</span>
          </div>
          {/* Coin Amount */}
          <div
            onClick={
              isDemoUser
                ? undefined
                : () => {
                    onClose();
                    navigate('/coins');
                  }
            }
            className={`flex items-center justify-between rounded bg-[#1f2c38] mx-4 my-[5px] px-[10px] py-[5px] text-white gap-[10px] ${isDemoUser ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <span className="text-lg">{t('profileDrawer.coinAmount')}</span>
            <span className="text-lg text-[#32d338]">{coinAmount.toFixed(2)}</span>
          </div>
          {/* Profile Menu */}
          <div className="px-4 py-[10px]">
            {isAuthenticated &&
              !isDemoUser &&
              (isMasterOwnerUser ? systemSettings?.deposit?.selection : true) && (
                <NavLink
                  to="/deposit"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `p-[3px] mb-[2px] text-sm font-semibold flex items-center hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] rounded-tl-[50px] rounded-bl-[50px] ${
                      isActive ? 'bg-[linear-gradient(90deg,#fec733,#18162a00)]' : ''
                    }`
                  }
                >
                  <div className="bg-sideMenuBg size-[35px] rounded-[50px] mr-[7px] flex items-center justify-center">
                    <Icons.Deposit className="text-green-400" size={18} />
                  </div>
                  <span className="font-semibold text-[#1f2c38] dark:text-white">
                    {t('profileDrawer.deposit')}
                  </span>
                </NavLink>
              )}
            {isAuthenticated &&
              !isDemoUser &&
              (isMasterOwnerUser ? systemSettings?.withdrawal?.selection : true) && (
                <NavLink
                  to="/withdraw"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `p-[3px] mb-[2px] text-sm font-semibold flex items-center hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] rounded-tl-[50px] rounded-bl-[50px] ${
                      isActive ? 'bg-[linear-gradient(90deg,#fec733,#18162a00)]' : ''
                    }`
                  }
                >
                  <div className="bg-sideMenuBg size-[35px] rounded-[50px] mr-[7px] flex items-center justify-center">
                    <Icons.Withdraw className="text-red-400" size={18} />
                  </div>
                  <span className="font-semibold text-[#1f2c38] dark:text-white">
                    {t('profileDrawer.withdraw')}
                  </span>
                </NavLink>
              )}
            {(isDemoUser
              ? profileDrawerItems.filter((item) => item.id === 7)
              : profileDrawerItems
            ).map((item) => (
              <NavLink
                key={item.id}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `p-[3px] mb-[2px] text-sm font-semibold flex items-center hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] rounded-tl-[50px] rounded-bl-[50px] ${
                    isActive ? 'bg-[linear-gradient(90deg,#fec733,#18162a00)]' : ''
                  }`
                }
              >
                <div className="bg-sideMenuBg size-[35px] rounded-[50px] mr-[7px] flex items-center justify-center">
                  <img className="size-[18px]" src={item.imgUrl} alt="" />
                </div>
                <span className="font-semibold text-[#1f2c38] dark:text-white">
                  {t(
                    `profileDrawer.items.${profileDrawerItemI18nKey[item.id] ?? ''}`,
                    { defaultValue: item.title }
                  )}
                </span>
              </NavLink>
            ))}
          </div>
          {/* Logout */}
          <div
            onClick={handleClick}
            className="flex h-10 mx-4 my-[5px] items-center rounded bg-[#781200] px-[10px] py-[5px] text-white transition cursor-pointer hover:opacity-90"
          >
            <img className="size-[18px] mr-[6px]" src="/images/logout.svg" alt="" />
            <span className="font-medium">{t('profileDrawer.logout')}</span>
          </div>
        </div>
      </div>
      <UnsettledBetModal open={exposureModalOpen} onClose={() => setExposureModalOpen(false)} />
    </>,
    document.body
  );
};

export default ProfileDrawer;
